import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';
import DeliverySearchModal from './DeliverySearchModal';
import DeliveryDetailsModal from './DeliveryDetailsModal';

const API_URL = process.env.REACT_APP_API_URL || '';
const SEARCH_DELAY_MS = 300;
const SEARCH_RESULT_LIMIT = 5;

function getRequestError(error, fallbackMessage) {
  if (error?.response?.status === 401) {
    return 'Tu sesión venció. Inicia sesión nuevamente para buscar tus entregas.';
  }

  return error?.response?.data?.error || fallbackMessage;
}

function DeliverySearchButton() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const searchRequestRef = useRef(0);
  const detailControllerRef = useRef(null);
  const normalizedQuery = query.trim();
  const canSearch = normalizedQuery.length >= 2 || /^\d$/.test(normalizedQuery);

  useEffect(() => {
    if (!isSearchOpen || !canSearch) {
      searchRequestRef.current += 1;
      setIsSearching(false);
      setSearchError('');
      setResults([]);
      return undefined;
    }

    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    const controller = new AbortController();

    setIsSearching(true);
    setSearchError('');
    setResults([]);

    const timeoutId = window.setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/api/user/delivery-points/search`,
          {
            params: { q: normalizedQuery, limit: SEARCH_RESULT_LIMIT },
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          }
        );

        if (searchRequestRef.current !== requestId) return;

        setResults((response.data.results || []).slice(0, SEARCH_RESULT_LIMIT));
      } catch (error) {
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;
        if (searchRequestRef.current !== requestId) return;

        setResults([]);
        setSearchError(
          getRequestError(error, 'No pudimos buscar tus entregas. Intenta nuevamente.')
        );
      } finally {
        if (searchRequestRef.current === requestId) {
          setIsSearching(false);
        }
      }
    }, SEARCH_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [canSearch, isSearchOpen, normalizedQuery]);

  useEffect(
    () => () => {
      detailControllerRef.current?.abort();
    },
    []
  );

  const openSearch = useCallback(() => {
    setSearchError('');
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    searchRequestRef.current += 1;
    setIsSearchOpen(false);
    setQuery('');
    setResults([]);
    setSearchError('');
    setIsSearching(false);
  }, []);

  const handleQueryChange = useCallback((nextQuery) => {
    searchRequestRef.current += 1;
    setQuery(nextQuery);
    setResults([]);
    setSearchError('');
  }, []);

  const selectDelivery = useCallback(async (deliveryId) => {
    detailControllerRef.current?.abort();
    const controller = new AbortController();
    detailControllerRef.current = controller;

    setIsSearchOpen(false);
    setIsDetailOpen(true);
    setSelectedDelivery(null);
    setDetailError('');
    setIsDetailLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/user/delivery-points/${deliveryId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        }
      );

      setSelectedDelivery(response.data.delivery || response.data);
    } catch (error) {
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;

      setDetailError(
        getRequestError(error, 'No pudimos cargar el detalle de esta entrega.')
      );
    } finally {
      if (detailControllerRef.current === controller) {
        detailControllerRef.current = null;
        setIsDetailLoading(false);
      }
    }
  }, []);

  const closeDetail = useCallback(() => {
    detailControllerRef.current?.abort();
    detailControllerRef.current = null;
    setIsDetailOpen(false);
    setSelectedDelivery(null);
    setDetailError('');
    setIsDetailLoading(false);
    setQuery('');
    setResults([]);
  }, []);

  const returnToResults = useCallback(() => {
    detailControllerRef.current?.abort();
    detailControllerRef.current = null;
    setIsDetailOpen(false);
    setSelectedDelivery(null);
    setDetailError('');
    setIsDetailLoading(false);
    setIsSearchOpen(true);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={openSearch}
        className="group relative rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#B1336E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E] focus-visible:ring-offset-2"
        aria-label="Buscar entrega"
      >
        <Search className="h-5 w-5" aria-hidden="true" />
        <span className="pointer-events-none absolute right-0 top-full z-50 mt-2 whitespace-nowrap rounded bg-slate-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 font-montserrat">
          Buscar Entrega
        </span>
      </button>

      <DeliverySearchModal
        isOpen={isSearchOpen}
        query={query}
        results={results}
        isSearching={isSearching}
        error={searchError}
        canSearch={canSearch}
        onQueryChange={handleQueryChange}
        onSelect={selectDelivery}
        onClose={closeSearch}
      />

      <DeliveryDetailsModal
        isOpen={isDetailOpen}
        delivery={selectedDelivery}
        isLoading={isDetailLoading}
        error={detailError}
        onBack={returnToResults}
        onClose={closeDetail}
      />
    </>
  );
}

export default DeliverySearchButton;
